import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowId, GridRowModes, GridRowModesModel } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import PlaceIdCoordinateLookupHelper, {
  PLACE_ID_CONDITION_KEY,
} from '~/components/Promo/PlaceIdCoordinateLookupHelper';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';

import {
  ApplyTypeEditCell,
  ConditionApplyType,
  ConditionTypeEditCell,
  RewardFilterEditCell,
  TextEditCell,
  defaultConditionApplyTypes,
  formatApplyTypeLabel,
  formatConditionLabel,
  voucherConditionTypes,
} from './VoucherConditionEditCells';

type VoucherCondition = definitions['VoucherConditionV1'];
type VoucherReward = definitions['VoucherRewardV1'];

type Props = {
  conditions: Array<VoucherCondition>;
  rewards: Array<VoucherReward>;
  onChange: (conditions: Array<VoucherCondition>) => void;
  disabled?: boolean;
  showAdvanceDescription?: boolean;
  /** When set, only these condition type keys (plus any types already used in rows) appear in the type picker. */
  allowedConditionTypeKeys?: Array<string>;
  includeRewardIdFilters?: boolean;
};

export default function VoucherConditions({
  conditions,
  rewards,
  onChange,
  showAdvanceDescription,
  disabled = false,
  allowedConditionTypeKeys,
  includeRewardIdFilters = true,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { promoMeta } = usePromoMeta();
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [newConditionIds, setNewConditionIds] = useState<Array<string>>([]);

  const voucherConditionTypeConfigs = useMemo(
    () => promoMeta?.voucher_condition_types ?? [],
    [promoMeta?.voucher_condition_types],
  );
  const voucherConditionTypeOptions = useMemo(() => {
    const fromMeta =
      voucherConditionTypeConfigs.length > 0
        ? voucherConditionTypeConfigs.map((config) => config.key)
        : voucherConditionTypes;
    const inUse = new Set(conditions.map((c) => c.condition_type));
    if (!allowedConditionTypeKeys?.length) {
      return fromMeta;
    }
    const allow = new Set(allowedConditionTypeKeys);
    const filtered = fromMeta.filter((key) => allow.has(key) || inUse.has(key));
    return filtered.length > 0 ? filtered : fromMeta;
  }, [allowedConditionTypeKeys, conditions, voucherConditionTypeConfigs]);

  const voucherConditionTypeConfigMap = useMemo(() => {
    const map = new Map<VoucherCondition['condition_type'], (typeof voucherConditionTypeConfigs)[number]>();
    voucherConditionTypeConfigs.forEach((config) => {
      map.set(config.key, config);
    });
    return map;
  }, [voucherConditionTypeConfigs]);

  const conditionApplyTypeMap = useMemo(() => {
    const map = new Map<VoucherCondition['condition_type'], Array<ConditionApplyType>>();
    voucherConditionTypeConfigs.forEach((config) => {
      map.set(config.key, config.conditionApplyTypes ?? []);
    });
    return map;
  }, [voucherConditionTypeConfigs]);

  useEffect(() => {
    setNewConditionIds((prev) =>
      prev.filter((id) => conditions.some((condition) => condition.id_voucher_condition === id)),
    );
  }, [conditions]);

  const rewardOptions = useMemo(() => {
    const baseOptions = rewards.map((reward, index) => ({
      id: reward.id_voucher_reward,
      label: `Reward ${index + 1}${Number.isFinite(reward.reward_amount) ? ` (${reward.reward_amount})` : ''}`,
    }));

    const existingIds = new Set(baseOptions.map((option) => option.id));
    const extras = new Map<string, string>();
    conditions.forEach((condition) => {
      (condition.voucher_reward_ids ?? []).forEach((rewardId) => {
        if (!existingIds.has(rewardId)) {
          extras.set(rewardId, `Unknown Reward (${rewardId})`);
        }
      });
    });

    const extraOptions = Array.from(extras.entries()).map(([id, label]) => ({ id, label }));
    return [...baseOptions, ...extraOptions];
  }, [conditions, rewards]);

  const handleAddCondition = useCallback(() => {
    const newId = uuid();
    const defaultConditionType = voucherConditionTypeOptions[0] ?? voucherConditionTypes[0];
    const configuredApplyTypes = conditionApplyTypeMap.get(defaultConditionType) ?? [];
    const defaultApplyTypes = configuredApplyTypes.length > 0 ? configuredApplyTypes : defaultConditionApplyTypes;
    onChange([
      ...conditions,
      {
        id_voucher_condition: newId,
        condition_type: defaultConditionType,
        condition_value: '',
        condition_apply_type: defaultApplyTypes[0] ?? 'NONE',
        voucher_reward_ids: [],
        customized_message: '',
      },
    ]);
    setNewConditionIds((prev) => [...prev, newId]);
    setRowModesModel((prev) => ({ ...prev, [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'condition_value' } }));
  }, [conditionApplyTypeMap, conditions, onChange, voucherConditionTypeOptions]);

  const handleEditClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  }, []);

  const handleSaveClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  }, []);

  const handleCancelClick = useCallback(
    (id: GridRowId) => {
      if (newConditionIds.includes(String(id))) {
        onChange(conditions.filter((condition) => condition.id_voucher_condition !== id));
        setNewConditionIds((prev) => prev.filter((conditionId) => conditionId !== id));
      }
      setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }));
    },
    [conditions, newConditionIds, onChange],
  );

  const handleDeleteCondition = useCallback(
    (conditionId: string) => {
      const next = conditions.filter((condition) => condition.id_voucher_condition !== conditionId);
      onChange(next);
    },
    [conditions, onChange],
  );

  const validateCondition = useCallback(
    (condition: VoucherCondition) => {
      if (!condition.condition_type) {
        enqueueSnackbar('Condition type is required', { variant: 'error' });
        return false;
      }
      if (!condition.condition_value?.trim()) {
        enqueueSnackbar('Condition value is required', { variant: 'error' });
        return false;
      }
      return true;
    },
    [enqueueSnackbar],
  );

  const processRowUpdate = useCallback(
    (updatedRow: VoucherCondition) => {
      // Strip virtual DataGrid fields that would cause API validation errors
      const { condition_details: _virtualGridField, ...cleanRow } = updatedRow as VoucherCondition & {
        condition_details?: unknown;
      };
      void _virtualGridField;

      const configuredApplyTypes = conditionApplyTypeMap.get(cleanRow.condition_type) ?? [];
      const allowedApplyTypes = configuredApplyTypes.length > 0 ? configuredApplyTypes : defaultConditionApplyTypes;
      const currentApplyType = cleanRow.condition_apply_type ?? 'NONE';
      const normalizedApplyType = allowedApplyTypes.includes(currentApplyType)
        ? currentApplyType
        : allowedApplyTypes[0] ?? 'NONE';
      const normalizedRow =
        normalizedApplyType === cleanRow.condition_apply_type
          ? cleanRow
          : { ...cleanRow, condition_apply_type: normalizedApplyType };

      if (!validateCondition(normalizedRow)) {
        throw new Error('Validation failed');
      }
      const next = conditions.map((condition) =>
        condition.id_voucher_condition === normalizedRow.id_voucher_condition ? normalizedRow : condition,
      );
      onChange(next);
      setNewConditionIds((prev) => prev.filter((id) => id !== normalizedRow.id_voucher_condition));
      enqueueSnackbar('Condition saved', { variant: 'success' });
      return normalizedRow;
    },
    [conditionApplyTypeMap, conditions, enqueueSnackbar, onChange, validateCondition],
  );

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update condition', { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const isAnyRowEditing = Object.values(rowModesModel).some((model) => model.mode === GridRowModes.Edit);

  /** True while a row is in edit mode and the type picker is set to PLACE-ID (parent `conditions` updates only on save). */
  const [placeIdTypeSelectedInEdit, setPlaceIdTypeSelectedInEdit] = useState(false);
  useEffect(() => {
    if (!isAnyRowEditing) {
      setPlaceIdTypeSelectedInEdit(false);
    }
  }, [isAnyRowEditing]);

  const handleConditionTypeSelectWhileEditing = useCallback((nextType: string) => {
    setPlaceIdTypeSelectedInEdit(nextType === PLACE_ID_CONDITION_KEY);
  }, []);

  const columns = useMemo<Array<GridColDef<VoucherCondition>>>(
    () =>
      [
        {
          field: 'condition_type',
          headerName: 'Condition Type',
          minWidth: 220,
          flex: 2,
          editable: true,
          renderCell: (params) => (params.row.condition_type ? formatConditionLabel(params.row.condition_type) : '-'),
          renderEditCell: (params) => (
            <ConditionTypeEditCell
              {...params}
              conditionTypes={voucherConditionTypeOptions}
              onConditionTypeChange={handleConditionTypeSelectWhileEditing}
            />
          ),
        },
        ...(showAdvanceDescription || isAnyRowEditing
          ? ([
              {
                field: 'condition_details',
                headerName: 'Details',
                minWidth: 240,
                flex: 3,
                editable: true,
                valueGetter: (_value: unknown, row: VoucherCondition) => row.condition_type,
                valueSetter: (value: unknown, row: VoucherCondition) => row,
                renderCell: (params) => {
                  const config = params.row.condition_type
                    ? voucherConditionTypeConfigMap.get(params.row.condition_type)
                    : undefined;
                  const details = config?.longDescription || config?.name;
                  return details ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                      {details}
                    </Typography>
                  ) : (
                    '-'
                  );
                },
                renderEditCell: (params) => {
                  const conditionType = (params.value ??
                    params.row.condition_type) as VoucherCondition['condition_type'];
                  const config = conditionType ? voucherConditionTypeConfigMap.get(conditionType) : undefined;
                  const details = config?.longDescription || config?.name;
                  return details ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'normal', wordBreak: 'break-word', p: 1 }}
                    >
                      {details}
                    </Typography>
                  ) : (
                    '-'
                  );
                },
              },
            ] as Array<GridColDef<VoucherCondition>>)
          : []),

        {
          field: 'condition_apply_type',
          headerName: 'Apply Type',
          minWidth: 140,
          flex: 1,
          editable: true,
          renderCell: (params) =>
            params.row.condition_apply_type ? formatApplyTypeLabel(params.row.condition_apply_type) : '-',
          renderEditCell: (params) => (
            <ApplyTypeEditCell
              {...params}
              applyTypes={conditionApplyTypeMap.get(params.row.condition_type) ?? defaultConditionApplyTypes}
            />
          ),
        },
        {
          field: 'condition_value',
          headerName: 'Condition Value',
          minWidth: 160,
          flex: 2,
          editable: true,
          renderCell: (params) => (
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {params.row.condition_value || '-'}
            </Typography>
          ),
          renderEditCell: (params) => <TextEditCell {...params} />,
        },
      ]
        .concat(
          includeRewardIdFilters
            ? [
                {
                  field: 'voucher_reward_ids',
                  headerName: 'Reward Filters',
                  minWidth: 200,
                  flex: 2,
                  editable: true,
                  renderCell: (params) => {
                    const ids = params.row.voucher_reward_ids ?? [];
                    if (!ids.length) {
                      return '-';
                    }
                    const labels = ids.map((id) => rewardOptions.find((option) => option.id === id)?.label || id);
                    return labels.join(', ');
                  },
                  renderEditCell: (params) => <RewardFilterEditCell {...params} rewardOptions={rewardOptions} />,
                },
              ]
            : [],
        )
        .concat([
          {
            field: 'customized_message',
            headerName: 'Custom Message',
            minWidth: 200,
            flex: 2,
            editable: true,
            renderCell: (params) => params.row.customized_message || '-',
            renderEditCell: (params) => <TextEditCell {...params} />,
          },
          {
            field: 'actions',
            headerName: '',
            width: 140,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
              const isEditing = rowModesModel[params.id]?.mode === GridRowModes.Edit;
              return (
                <Stack direction="row" spacing={1}>
                  {isEditing ? (
                    <>
                      <IconButton
                        aria-label="Save condition"
                        size="small"
                        type="button"
                        onClick={() => handleSaveClick(params.id)}
                        disabled={disabled}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Cancel condition edit"
                        size="small"
                        type="button"
                        onClick={() => handleCancelClick(params.id)}
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        aria-label="Edit condition"
                        size="small"
                        type="button"
                        onClick={() => handleEditClick(params.id)}
                        disabled={disabled}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Delete condition"
                        size="small"
                        type="button"
                        onClick={() => handleDeleteCondition(params.row.id_voucher_condition || '')}
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Stack>
              );
            },
          },
        ]),
    [
      conditionApplyTypeMap,
      disabled,
      handleCancelClick,
      handleDeleteCondition,
      handleEditClick,
      handleConditionTypeSelectWhileEditing,
      handleSaveClick,
      includeRewardIdFilters,
      isAnyRowEditing,
      rewardOptions,
      rowModesModel,
      showAdvanceDescription,
      voucherConditionTypeConfigMap,
      voucherConditionTypeOptions,
    ],
  );

  const hasPlaceIdCondition = conditions.some((c) => (c.condition_type as string) === PLACE_ID_CONDITION_KEY);
  const showPlaceIdHelper = hasPlaceIdCondition || placeIdTypeSelectedInEdit;

  return (
    <Box>
      {showAdvanceDescription && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          All Conditions need to be met to receive the voucher.
        </Typography>
      )}
      <DataGrid
        autoHeight
        rows={conditions}
        columns={columns}
        getRowId={(row) => row.id_voucher_condition}
        getRowHeight={() => 'auto'}
        disableRowSelectionOnClick
        hideFooter
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        sx={{ backgroundColor: 'background.paper' }}
      />
      {showPlaceIdHelper && (
        <Box mt={2}>
          <PlaceIdCoordinateLookupHelper />
        </Box>
      )}
      <Box mt={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddCondition}
          disabled={disabled}
        >
          Add condition
        </Button>
      </Box>
    </Box>
  );
}
